import { useCallback, useEffect, useState, useRef } from 'react';
import { Box, Button, CircularProgress, IconButton, ListItem, Modal, TextField, Typography, Chip, Tooltip, Dialog, DialogContent, Fade, Snackbar, Alert, useMediaQuery, Menu, MenuItem, ListItemIcon, ListItemText, Divider, Stack, Autocomplete, InputAdornment, FormControlLabel, Checkbox, Pagination } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ImageIcon from '@mui/icons-material/Image';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import { alpha } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import { FixedSizeList as VirtualList } from 'react-window';
import { Add, Refresh } from '@mui/icons-material';
import ArticuloService from '../../services/articulos';
import WhatsappSessionsService from '../../services/whatsappSessions';
import { useWhatsappSessions } from '../../components/base/WhatsappSessionsProvider';
import CustomerService from '../../services/customers';
import ImagenesService from '../../services/imagenes';
import ImagenesAIService from '../../services/imagenesAI';
import { IMAGES_HOST } from '../../config/config';
// FormArticulo ya no se usa aquí; NewPublication maneja la creación.
import NewPublication from './NewPublication';
import { ccyFormat } from '../../helpers/helper';
import { OPERATION } from '../../helpers/enums';
import Banner from '../../components/Banner';
import WhatsappSessionGuard from '../../components/WhatsappSessionGuard';
import { UploadImage } from '../../components';
import React from 'react';
import logo from '../../assets/images/jazminmya.png';

// Componente principal para gestionar publicaciones de artículos existentes y creación de nuevos.
// Props:
//  - store: objeto con _id (opcional) para filtrar artículos por tienda.
//  - onArticleCreated: callback(article) cuando se crea un nuevo artículo.
//  - withImages: boolean para permitir imágenes en creación.
//  - simplePrices: boolean para decidir si mostrar precios en modal interno.
const Publications = ({ store, onArticleCreated, withImages = false, simplePrices = true }) => {
  // Intentar obtener contexto multi-sesión; si el provider no está presente capturamos la excepción en efecto secundario.
  const { sessionId } = useWhatsappSessions();
  const [articles, setArticles] = useState([]);          // Lista completa
  const [loading, setLoading] = useState(false);         // Cargando lista
  const [error, setError] = useState(null);              // Error global
  const [filter, setFilter] = useState('');              // Texto de búsqueda
  const [debouncedFilter, setDebouncedFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(null);
  const [openCreate, setOpenCreate] = useState(false);   // Modal creación
  const [openViewer, setOpenViewer] = useState(false);
  const [viewerImages, setViewerImages] = useState([]);
  const [viewerTitle, setViewerTitle] = useState('');
  const preloadRef = useRef({}); // cache de imágenes pre-cargadas
  const [snackbar, setSnackbar] = useState({ open: false, msg: '', severity: 'success' });
  const [whatsappPreview, setWhatsappPreview] = useState({ open: false, text: '', images: [] });
  // Eliminamos selección persistente: el click abre menú directamente
  const [menuAnchor, setMenuAnchor] = useState(null); // reservado si luego se quiere abrir con teclado
  const [menuItem, setMenuItem] = useState(null);
  const [menuPosition, setMenuPosition] = useState(null); // {mouseX, mouseY}
  const [mobileActionsOpen, setMobileActionsOpen] = useState(false);
  // Estado para edición
  const [openEdit, setOpenEdit] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editValues, setEditValues] = useState({ name: '', description: '', list_price: '', sale_price: '', customer_id: '', images: [], sku: '', hasWholesale: false, wholesaleDiscount: 200 });
  const [editNewImages, setEditNewImages] = useState([]); // Files seleccionados nuevos
  const [imagePrompt, setImagePrompt] = useState('');
  const [editRemovedIndexes, setEditRemovedIndexes] = useState([]); // índices de imágenes existentes a remover
  const [editUploadProgress, setEditUploadProgress] = useState(0);
  const [editOrder, setEditOrder] = useState([]); // orden de imágenes existentes por índice
  const [editNewImagePreviews, setEditNewImagePreviews] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const preloadImages = useCallback((images = []) => {
    images.forEach(src => {
      if (!src || preloadRef.current[src]) return;
      const img = new Image();
      img.src = src;
      preloadRef.current[src] = true;
    });
  }, []);

  const normalizePagedArticles = useCallback((rawData, requestedPage, requestedLimit) => {
    const list = Array.isArray(rawData)
      ? rawData
      : (rawData?.items || rawData?.docs || rawData?.results || rawData?.articles || rawData?.data || []);

    const pagination = rawData?.pagination || rawData?.meta || {};
    const normalizedPage = Number(rawData?.page || pagination?.page || requestedPage || 1);
    const normalizedLimit = Number(rawData?.limit || pagination?.limit || requestedLimit || 20);
    const normalizedTotalPages = Number(rawData?.totalPages || pagination?.totalPages);
    const normalizedTotal = Number(rawData?.total || rawData?.totalItems || pagination?.total || pagination?.totalItems);

    // Si el backend no devuelve metadata, inferimos si existe una página siguiente.
    const inferredTotalPages = list.length === normalizedLimit ? normalizedPage + 1 : normalizedPage;

    return {
      items: Array.isArray(list) ? list : [],
      page: Number.isFinite(normalizedPage) && normalizedPage > 0 ? normalizedPage : requestedPage,
      totalPages: Number.isFinite(normalizedTotalPages) && normalizedTotalPages > 0
        ? normalizedTotalPages
        : Math.max(1, inferredTotalPages),
      total: Number.isFinite(normalizedTotal) && normalizedTotal >= 0 ? normalizedTotal : null,
    };
  }, []);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const pageSize = isMobile ? 15 : 30;

  const loadArticles = useCallback(async (targetPage = 1) => {
    try {
      setLoading(true);
      setError(null);

      if (store?._id) {
        const params = new URLSearchParams({
          to: OPERATION.VENDER,
          onlyStock: 'false',
          page: String(targetPage),
          limit: String(pageSize),
          offers: 'true'
        });
        if (debouncedFilter.trim()) params.set('name', debouncedFilter.trim());

        const data = await ArticuloService.searchArticlesInStore2(store._id, params);
        const normalized = normalizePagedArticles(data, targetPage, pageSize);

        if (targetPage > 1 && normalized.items.length === 0) {
          setPage(prev => Math.max(1, prev - 1));
          return;
        }

        setArticles(normalized.items);
        setTotalPages(Math.max(1, normalized.totalPages));
        setTotalItems(normalized.total);
        return;
      }

      // Fallback para uso sin store: intentamos endpoint paginado global.
      try {
        const data = await ArticuloService.getAllPaginated({
          page: targetPage,
          limit: pageSize,
          name: debouncedFilter.trim()
        });
        const normalized = normalizePagedArticles(data, targetPage, pageSize);

        if (targetPage > 1 && normalized.items.length === 0) {
          setPage(prev => Math.max(1, prev - 1));
          return;
        }

        setArticles(normalized.items);
        setTotalPages(Math.max(1, normalized.totalPages));
        setTotalItems(normalized.total);
      } catch {
        // Último fallback: paginado en cliente si el backend no soporta parámetros.
        const all = await ArticuloService.getAll();
        const allList = Array.isArray(all) ? all : [];
        const txt = debouncedFilter.toLowerCase().trim();
        const filtered = !txt ? allList : allList.filter(a => {
          const name = a.article?.name || '';
          const sku = a.sku || a.article?.sku || '';
          const categoryName = a.category?.name || '';
          return name.toLowerCase().includes(txt) ||
            sku.toLowerCase().includes(txt) ||
            categoryName.toLowerCase().includes(txt);
        });

        const computedTotalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
        const safePage = Math.min(targetPage, computedTotalPages);
        const from = (safePage - 1) * pageSize;
        const to = from + pageSize;

        setArticles(filtered.slice(from, to));
        setTotalPages(computedTotalPages);
        setTotalItems(filtered.length);
        if (safePage !== targetPage) setPage(safePage);
      }
    } catch (err) {
      setError(err?.message || 'Error al cargar artículos');
    } finally {
      setLoading(false);
    }
  }, [store, pageSize, debouncedFilter, normalizePagedArticles]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilter(filter);
    }, 300);

    return () => clearTimeout(timer);
  }, [filter]);

  useEffect(() => {
    setPage(1);
  }, [store?._id, pageSize, debouncedFilter]);

  useEffect(() => {
    loadArticles(page);
  }, [loadArticles, page]);

  // Sin selección persistente: no se requiere efecto de reseteo

  // La lógica de creación ahora está en NewPublication.onSuccess

  useEffect(() => {
    const nextPreviews = editNewImages.map((file) => ({
      file,
      url: URL.createObjectURL(file)
    }));
    setEditNewImagePreviews(nextPreviews);

    return () => {
      nextPreviews.forEach((item) => URL.revokeObjectURL(item.url));
    };
  }, [editNewImages]);

  // Cargar proveedores on demand al abrir edición
  const loadCustomers = useCallback(async () => {
    try {
      setLoadingCustomers(true);
      const data = await CustomerService.getAll();
      setCustomers(data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingCustomers(false);
    }
  }, []);

  const openEditModal = useCallback((item) => {
    // item structure: article (name, description), prices(list_price,sale_price), customer
    setEditValues({
      id: item._id,
      name: item.article?.name || '',
      description: item.article?.description || '',
      list_price: item.prices?.list_price ?? '',
      sale_price: item.prices?.sale_price ?? '',
      sku: item.sku || item.article?.sku || '',
      customer_id: item.customer?._id || '',
      images: item.article?.images || [],
      articleid: item.article._id,
      hasWholesale: false,
      wholesaleDiscount: 200
    });
    setEditNewImages([]);
    setImagePrompt('');
    setEditRemovedIndexes([]);
    setEditUploadProgress(0);
    // inicializar órdenes
    setEditOrder(item.article?.images ? item.article.images.map((_, idx) => idx) : []);
    setOpenEdit(true);
  }, []);

  const handleEditChange = (field, value) => {
    setEditValues(v => ({ ...v, [field]: value }));
  };

  const resolvePreviewImageUrl = useCallback((src) => {
    if (!src) return '';
    if (src.startsWith('http://') || src.startsWith('https://') || src.startsWith('data:image/')) return src;
    return `${IMAGES_HOST}/IA/${src}.jpg`;
  }, []);

  const normalizeSkuForWhatsapp = useCallback((sku = '') => {
    const value = String(sku || '').trim();
    if (!value) return '';

    const match = value.match(/^([^0-9]*)([0-9]+)$/);
    if (!match) return value;

    const prefix = match[1] || '';
    const numericPart = match[2] || '';
    const cleanedNumeric = String(Number(numericPart));
    return `${prefix}${cleanedNumeric === 'NaN' ? numericPart : cleanedNumeric}`;
  }, []);

  const buildWhatsappPreview = useCallback(({ typeLabel, name, description, salePrice, hasWholesale, wholesaleDiscount, sku, images = [] }) => {
    const fmtMoney = (n) => `$${Number(n).toLocaleString('es-AR')}`;
    let priceLines = '';

    if (salePrice) {
      if (hasWholesale) {
        const mayor = Math.max(0, Number(salePrice) - Number(wholesaleDiscount || 0));
        priceLines = `\n\n✔️${fmtMoney(salePrice)} X UNIDAD \n✔️${fmtMoney(mayor)} X MAYOR`;
      } else {
        priceLines = `\n\n✔️${fmtMoney(salePrice)}`;
      }
    }

    const normalizedSku = normalizeSkuForWhatsapp(sku || '');
    const skuLine = normalizedSku ? `\n\n${normalizedSku}` : '';
    const messageBody = `${typeLabel}\n\n${name || ''}\n\n${description || ''}${priceLines}`;
    const resolvedImages = (images || []).map((img) => resolvePreviewImageUrl(img)).filter(Boolean);

    return {
      text: `${messageBody}${skuLine}`,
      images: resolvedImages
    };
  }, [normalizeSkuForWhatsapp, resolvePreviewImageUrl]);

  const dataUrlToBlob = useCallback(async (value) => {
    const resp = await fetch(value);
    return resp.blob();
  }, []);

  const base64ToBlob = useCallback((base64, contentType = 'image/png') => {
    const clean = String(base64 || '').replace(/^data:[^;]+;base64,/, '');
    const binary = atob(clean);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new Blob([bytes], { type: contentType });
  }, []);

  const resolveBlobFromProxyPayload = useCallback(async (payload) => {
    if (!payload) return null;
    if (payload instanceof Blob) return payload;

    if (Array.isArray(payload) && payload.length > 0) {
      return resolveBlobFromProxyPayload(payload[0]);
    }

    if (typeof payload === 'string') {
      if (payload.startsWith('data:image/')) {
        return dataUrlToBlob(payload);
      }
      if (payload.startsWith('http://') || payload.startsWith('https://')) {
        const resp = await fetch(payload, { mode: 'cors' });
        if (!resp.ok) throw new Error('El proxy devolvió una URL pero no se pudo descargar');
        return resp.blob();
      }
      // fallback para base64 puro sin prefijo data:image
      return base64ToBlob(payload);
    }

    if (typeof payload === 'object') {
      const candidates = [
        payload.data,
        payload.image,
        payload.imageUrl,
        payload.url,
        payload.file,
        payload.base64,
        payload.src,
      ];

      for (const candidate of candidates) {
        if (!candidate) continue;
        const resolved = await resolveBlobFromProxyPayload(candidate);
        if (resolved) return resolved;
      }
    }

    return null;
  }, [base64ToBlob, dataUrlToBlob]);

  const copyImageUrlToClipboard = useCallback(async (url) => {
    if (!url) throw new Error('Imagen no disponible');
    if (!navigator.clipboard?.write || typeof ClipboardItem === 'undefined') {
      throw new Error('Tu navegador no soporta copiar imágenes al portapapeles');
    }

    let blob;
    try {
      const response = await fetch(url, { mode: 'cors' });
      if (!response.ok) {
        throw new Error('No se pudo descargar la imagen para copiarla');
      }
      blob = await response.blob();
    } catch (directErr) {
      // Fallback: pedir al backend que descargue la imagen y la devuelva en formato reusable.
      const proxyPayload = await ImagenesService.download({ image: url, url, imageUrl: url, images: [url] });
      blob = await resolveBlobFromProxyPayload(proxyPayload);
      if (!blob) {
        throw new Error('No se pudo copiar la imagen por CORS. El backend no devolvió un formato válido.');
      }
    }

    const mimeType = blob.type || 'image/png';
    await navigator.clipboard.write([
      new ClipboardItem({ [mimeType]: blob })
    ]);
  }, [resolveBlobFromProxyPayload]);

  const handleOpenWhatsappPrepared = useCallback(() => {
    const message = (whatsappPreview.text || '').trim();
    if (!message) {
      setSnackbar({ open: true, msg: 'No hay mensaje para abrir en WhatsApp', severity: 'warning' });
      return;
    }

    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    const waTab = window.open(whatsappUrl, '_blank', 'noopener,noreferrer');

    const resolvedImages = (whatsappPreview.images || [])
      .map((img) => resolvePreviewImageUrl(img))
      .filter(Boolean);

    let openedImages = 0;
    resolvedImages.forEach((imgUrl) => {
      const tab = window.open(imgUrl, '_blank', 'noopener,noreferrer');
      if (tab) openedImages += 1;
    });

    if (!waTab) {
      setSnackbar({ open: true, msg: 'El navegador bloqueó la apertura de WhatsApp (habilitá popups).', severity: 'warning' });
      return;
    }

    if (resolvedImages.length && openedImages < resolvedImages.length) {
      setSnackbar({
        open: true,
        msg: `WhatsApp abierto. Se abrieron ${openedImages}/${resolvedImages.length} imágenes (revisá bloqueo de popups).`,
        severity: 'info'
      });
      return;
    }

    setSnackbar({
      open: true,
      msg: resolvedImages.length ? 'WhatsApp e imágenes abiertas para adjuntar.' : 'WhatsApp abierto con el mensaje listo.',
      severity: 'success'
    });
  }, [resolvePreviewImageUrl, whatsappPreview.images, whatsappPreview.text]);

  const closeActionOverlays = useCallback(() => {
    setMenuAnchor(null);
    setMenuPosition(null);
    setMobileActionsOpen(false);
  }, []);

  const handleOpenRowActions = useCallback((event, item) => {
    setMenuItem(item);
    if (isMobile) {
      setMobileActionsOpen(true);
      setMenuAnchor(null);
      setMenuPosition(null);
      return;
    }

    setMenuPosition({ mouseX: event.clientX + 2, mouseY: event.clientY - 6 });
    setMenuAnchor(null);
  }, [isMobile]);

  const handleViewImages = useCallback(() => {
    if (menuItem?.article?.images?.length) {
      setViewerImages(menuItem.article.images);
      setViewerTitle(menuItem.article.name || '');
      setOpenViewer(true);
    } else {
      setSnackbar({ open: true, msg: 'Sin imágenes para mostrar', severity: 'info' });
    }
    closeActionOverlays();
  }, [closeActionOverlays, menuItem]);

  const handleCopySku = useCallback(() => {
    if (menuItem) {
      const sku = menuItem.sku || menuItem.article?.sku || '';
      if (sku) navigator.clipboard?.writeText(sku);
      setSnackbar({ open: true, msg: sku ? 'SKU copiado' : 'SKU vacío', severity: sku ? 'success' : 'warning' });
    }
    closeActionOverlays();
  }, [closeActionOverlays, menuItem]);

  const handleRepublish = useCallback(() => {
    if (menuItem) openEditModal(menuItem);
    closeActionOverlays();
  }, [closeActionOverlays, menuItem, openEditModal]);

  const handleDeletePlaceholder = useCallback(() => {
    setSnackbar({ open: true, msg: 'Función eliminar (pendiente)', severity: 'info' });
    closeActionOverlays();
  }, [closeActionOverlays]);

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    // Validaciones simples
    if (!editValues.name || !editValues.description || !editValues.list_price || !editValues.sale_price) {
      setSnackbar({ open: true, msg: 'Complete los campos obligatorios', severity: 'warning' });
      return;
    }
    if (Number(editValues.sale_price) <= Number(editValues.list_price)) {
      setSnackbar({ open: true, msg: 'El precio debe ser mayor al costo', severity: 'warning' });
      return;
    }
    try {
      setEditLoading(true);
      // Subir nuevas imágenes si existen
      let uploaded = [];
      if (editNewImages.length) {
        const fd = new FormData();
        editNewImages.forEach(f => fd.append('image', f));
        if (imagePrompt.trim()) {
          fd.append('prompt', imagePrompt.trim());
          uploaded = await ImagenesAIService.generate(fd);
        } else {
          uploaded = await ImagenesService.upload(fd);
        }
      }
      // Construir array final de imágenes conservando orden (existentes - removidas + nuevas al final)
      // reordenar existentes acorde a editOrder, filtrando removidas
      const orderedExisting = editOrder
        .filter(idx => !editRemovedIndexes.includes(idx))
        .map(idx => editValues.images[idx]);
      // nuevas se agregan en orden definido (original secuencial + posibilidad de mover)
      const finalImages = [...orderedExisting, ...uploaded];
      const payload = {
        article: { name: editValues.name, description: editValues.description, images: finalImages },
        prices: { list_price: Number(editValues.list_price), sale_price: Number(editValues.sale_price) },
        customer: editValues.customer_id ? editValues.customer_id : undefined,
      };
  const updated = await ArticuloService.update(store?._id, editValues.id, payload);
  // Actualización optimista inmediata
  setArticles(prev => prev.map(a => a._id === editValues.id ? { ...a, article: { ...a.article, name: updated.article?.name, description: updated.article?.description, images: updated.article?.images || finalImages }, prices: { ...a.prices, ...updated.prices }, customer: updated.customer || a.customer } : a));
  setSnackbar({ open: true, msg: 'Artículo actualizado', severity: 'success' });
  // Generar formato de mensaje WhatsApp para copiar/pegar manualmente
  try {
    const preview = buildWhatsappPreview({
      typeLabel: 'Reingreso',
      name: editValues.name,
      description: editValues.description,
      salePrice: editValues.sale_price,
      hasWholesale: editValues.hasWholesale,
      wholesaleDiscount: editValues.wholesaleDiscount,
      sku: updated?.sku || editValues.sku || '',
      images: finalImages
    });
    setWhatsappPreview({ open: true, text: preview.text, images: preview.images });
    setSnackbar({ open: true, msg: 'Mensaje listo para copiar y pegar en WhatsApp', severity: 'info' });
  } catch (msgErr) {
    console.warn('No se pudo preparar mensaje WhatsApp:', msgErr);
    setSnackbar({ open: true, msg: 'Artículo actualizado, pero no se pudo preparar el mensaje', severity: 'warning' });
  }
  // Refrescar desde servidor para asegurar consistencia (por ej. cambios calculados en backend)
  await loadArticles();
  setOpenEdit(false);
    } catch (err) {
      setSnackbar({ open: true, msg: err.message || 'Error al actualizar', severity: 'error' });
    } finally {
      setEditLoading(false);
      setEditUploadProgress(0);
    }
  };

  return (
  <WhatsappSessionGuard enabled sessionId={sessionId}>
    <Box display='flex' flexDirection='column' height='100%' minHeight={0} p={{ xs: 1, sm: 2 }} gap={{ xs: 1.25, sm: 2 }}>
      <Box
        sx={{
          px: { xs: 1, sm: 1.5 },
          py: { xs: 1.2, sm: 1.4 },
          borderRadius: 2,
          color: '#1f2937',
          background: 'linear-gradient(120deg, #f8fafc 0%, #eef2ff 48%, #ecfeff 100%)',
          border: '1px solid #e5e7eb'
        }}
      >
        <Typography variant='h6' sx={{ fontWeight: 800, lineHeight: 1.1, fontSize: { xs: '1rem', sm: '1.15rem' } }}>
          Publicaciones
        </Typography>
        <Typography variant='body2' sx={{ mt: 0.35, color: '#4b5563' }}>
          Gestiona, republica y comparte productos desde una vista optimizada para mobile.
        </Typography>
      </Box>
      {/* Barra de acciones */}
  <Box
    display='flex'
    flexWrap='wrap'
    gap={{ xs: 1, sm: 2 }}
    alignItems='center'
    position='sticky'
    top={0}
    zIndex={2}
    bgcolor={alpha(theme.palette.background.paper, 0.9)}
    backdropFilter='blur(8px)'
    borderRadius={2}
    py={{ xs: 1, sm: 0 }}
    px={{ xs: 0.5, sm: 0 }}
    sx={{ borderBottom: { xs: '1px solid #efefef', sm: 'none' }, boxShadow: '0 6px 18px rgba(15, 23, 42, 0.06)' }}
  >
        <TextField
          label='Buscar artículos'
          placeholder='Nombre, SKU, Categoría'
          variant='outlined'
          size={isMobile ? 'medium' : 'small'}
          value={filter}
          onChange={e => setFilter(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchRoundedIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            )
          }}
          sx={{
            minWidth: { xs: '100%', sm: 240 },
            flex: { sm: 1 },
            maxWidth: { sm: 420 },
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: '#fff'
            }
          }}
        />
  <Button
    startIcon={<Add />}
    variant='contained'
    onClick={() => setOpenCreate(true)}
    fullWidth={isMobile}
    size={isMobile ? 'large' : 'medium'}
    sx={{
      order: { xs: 2, sm: 0 },
      borderRadius: 2,
      textTransform: 'none',
      fontWeight: 700,
      background: 'linear-gradient(90deg, #2563eb, #0891b2)',
      boxShadow: '0 10px 20px rgba(37, 99, 235, 0.24)',
      '&:hover': {
        background: 'linear-gradient(90deg, #1d4ed8, #0e7490)'
      }
    }}
  >Publicar nuevo</Button>
  {/* Botón manual de envío removido; ahora el envío es automático al crear o republicar */}
        <Tooltip title='Recargar lista'>
          <span>
            <IconButton
              disabled={loading}
              onClick={() => loadArticles(page)}
              size={isMobile ? 'large' : 'medium'}
              sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}
            >
              <Refresh />
            </IconButton>
          </span>
        </Tooltip>
        {loading && <CircularProgress size={24} />}
        {error && <Typography color='error' variant='caption'>{error}</Typography>}
        {snackbar.open && <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} 
        >
          <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.msg}
          </Alert>
        </Snackbar>}
        <Stack
          direction='row'
          spacing={0.75}
          flexWrap='wrap'
          sx={{ width: { xs: '100%', sm: 'auto' }, order: { xs: 3, sm: 0 } }}
        >
          <Chip label={totalItems != null ? `Total: ${totalItems}` : 'Total: --'} color='default' />
          <Chip label={`Mostrando: ${articles.length}`} color='primary' />
        </Stack>
        <Box ml={{ xs: 0, sm: 'auto' }} display='flex' alignItems='center' width={{ xs: '100%', sm: 'auto' }}>
          {sessionId && (
            <Tooltip title='Reiniciar sesión de WhatsApp (pedirá nuevo QR)'>
              <Button
                variant='outlined'
                color='warning'
                size={isMobile ? 'medium' : 'small'}
                fullWidth={isMobile}
                onClick={async () => {
                  try {
                    await WhatsappSessionsService.resetSession(sessionId);
                    setSnackbar({ open: true, msg: 'Sesión reiniciada. Escaneá el nuevo QR.', severity: 'info' });
                  } catch (e) {
                    setSnackbar({ open: true, msg: e?.message || 'Error reiniciando sesión', severity: 'error' });
                  }
                }}
                sx={{ whiteSpace: 'nowrap' }}
              >Reiniciar WhatsApp</Button>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Lista: virtualizada en desktop, simple en mobile para permitir auto-height */}
      <Box
        flex={1}
        minHeight={0}
        border='1px solid #e0e0e0'
        borderRadius={2}
        overflow='hidden'
        sx={{
          background: 'linear-gradient(180deg, #fafafa 0%, #ffffff 100%)',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {articles.length === 0 && !loading && <Typography p={2} variant='body2'>Sin resultados</Typography>}
        {articles.length > 0 && !isMobile && (
          <VirtualList
            height={Math.min(articles.length * 124, 620)}
            itemCount={articles.length}
            itemSize={124}
            width='100%'
            overscanCount={6}
          >
            {({ index, style }) => {
              const item = articles[index];
              return (
                <Box style={style} key={item._id} px={0.5}>
                  <PublicationRow
                    item={item}
                    preloadImages={preloadImages}
                    onOpenViewer={(images, title) => {
                      setViewerImages(images);
                      setViewerTitle(title);
                      setOpenViewer(true);
                    }}
                    isMobile={false}
                    rowIndex={index}
                    onRowClick={(event) => handleOpenRowActions(event, item)}
                  />
                </Box>
              );
            }}
          </VirtualList>
        )}
        {articles.length > 0 && isMobile && (
          <Box
            display='flex'
            flexDirection='column'
            sx={{
              overflowY: 'auto',
              maxHeight: '100%',
              WebkitOverflowScrolling: 'touch',
              pb: 0.5
            }}
          >
            {articles.map((item, idx) => (
              <PublicationRow
                key={item._id}
                item={item}
                preloadImages={preloadImages}
                onOpenViewer={(images, title) => {
                  setViewerImages(images);
                  setViewerTitle(title);
                  setOpenViewer(true);
                }}
                isMobile
                rowIndex={idx}
                onRowClick={(event) => handleOpenRowActions(event, item)}
              />
            ))}
          </Box>
        )}
      </Box>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent='space-between'
        flexWrap='wrap'
        gap={1}
      >
        <Typography variant='caption' color='text.secondary'>
          Página {page} de {Math.max(1, totalPages)}
        </Typography>
        <Pagination
          count={Math.max(1, totalPages)}
          page={Math.min(page, Math.max(1, totalPages))}
          onChange={(_, nextPage) => setPage(nextPage)}
          color='primary'
          size={isMobile ? 'small' : 'medium'}
          showFirstButton
          showLastButton
          disabled={loading}
          sx={{ alignSelf: { xs: 'center', sm: 'auto' } }}
        />
      </Stack>

      {/* Modal de nueva publicación independiente */}
      <Modal open={openCreate} onClose={() => setOpenCreate(false)}>
        <Box
          bgcolor='#fff'
          p={0}
          m='auto'
          mt={{ xs: 0, sm: 2 }}
          borderRadius={{ xs: 0, sm: 2 }}
          height={{ xs: '100dvh', sm: '94vh' }}
          maxHeight={{ xs: '100dvh', sm: '94vh' }}
          overflow='hidden'
          width={{ xs: '100%', sm: 760 }}
          display='flex'
          flexDirection='column'
        >
          <Box flex={1} minHeight={0} overflow='auto' sx={{ WebkitOverflowScrolling: 'touch' }}>
            <NewPublication
              store={store}
              navigateBack={() => setOpenCreate(false)}
              onSuccess={(saved, extra) => {
                onArticleCreated && onArticleCreated(saved);
                setOpenCreate(false);
                setPage(1);
                loadArticles(1);
                setSnackbar({ open: true, msg: 'Publicación creada correctamente', severity: 'success' });

                if (extra?.whatsappDraft) {
                  const preview = buildWhatsappPreview({
                    typeLabel: 'Ingreso',
                    name: extra.whatsappDraft.name,
                    description: extra.whatsappDraft.description,
                    salePrice: extra.whatsappDraft.salePrice,
                    hasWholesale: extra.whatsappDraft.hasWholesale,
                    wholesaleDiscount: extra.whatsappDraft.wholesaleDiscount,
                    sku: extra.whatsappDraft.sku,
                    images: extra.whatsappDraft.images
                  });
                  setWhatsappPreview({
                    open: true,
                    text: preview.text,
                    images: preview.images
                  });
                  return;
                }

                if (extra?.whatsappPreview?.text) {
                  // Compatibilidad con formato anterior.
                  setWhatsappPreview({
                    open: true,
                    text: extra.whatsappPreview.text,
                    images: Array.isArray(extra.whatsappPreview.images) ? extra.whatsappPreview.images : []
                  });
                }
              }}
              onError={(msg) => setSnackbar({ open: true, msg: msg || 'Error al crear publicación', severity: 'error' })}
            />
          </Box>
        </Box>
      </Modal>
      {/* Dialog visor de imágenes */}
      <Dialog
        open={openViewer}
        fullScreen={isMobile}
        fullWidth
        maxWidth='md'
        keepMounted
        onClose={() => setOpenViewer(false)}
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 150 }}
      >
        <DialogContent>
          <Box display='flex' flexDirection='column' gap={2}>
            <Typography variant='h6'>{viewerTitle}</Typography>
            {/* Reutilizar Banner para navegación de imágenes */}
            {/* Import dinámico si se quiere optimizar; aquí directo. */}
            {/* Altura mayor para mejor visualización */}
            {viewerImages.length > 0 && (
              <Box>
                {/* Banner ya implementa navegación; mantenido montado para evitar coste de recreación */}
                <Banner images={viewerImages} height={isMobile ? 240 : 320} onlyImages />
              </Box>
            )}
            <Box display='flex' justifyContent='flex-end'>
              <Button variant='outlined' onClick={() => setOpenViewer(false)}>Cerrar</Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Vista previa del mensaje para copiar/pegar en WhatsApp */}
      <Dialog
        open={whatsappPreview.open}
        fullScreen={isMobile}
        fullWidth
        maxWidth='sm'
        onClose={() => setWhatsappPreview({ open: false, text: '', images: [] })}
      >
        <DialogContent>
          <Box display='flex' flexDirection='column' gap={2}>
            <Typography variant='h6'>Mensaje WhatsApp (copiar y pegar)</Typography>
            <TextField
              multiline
              minRows={8}
              maxRows={16}
              value={whatsappPreview.text}
              fullWidth
              InputProps={{ readOnly: true }}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent='flex-end' gap={1}>
              <Button
                variant='contained'
                color='success'
                onClick={handleOpenWhatsappPrepared}
              >Abrir WhatsApp + imágenes</Button>
              <Button
                variant='outlined'
                onClick={async () => {
                  try {
                    await navigator.clipboard?.writeText(whatsappPreview.text || '');
                    setSnackbar({ open: true, msg: 'Mensaje copiado al portapapeles', severity: 'success' });
                  } catch {
                    setSnackbar({ open: true, msg: 'No se pudo copiar automáticamente', severity: 'warning' });
                  }
                }}
              >Copiar</Button>
              <Button variant='contained' onClick={() => setWhatsappPreview({ open: false, text: '', images: [] })}>Cerrar</Button>
            </Stack>
            {whatsappPreview.images?.length > 0 && (
              <Box>
                <Typography variant='subtitle2' sx={{ mb: 1 }}>Copiar imágenes como archivo</Typography>
                <Stack direction='row' gap={1} flexWrap='wrap'>
                  {whatsappPreview.images.map((imgUrl, idx) => (
                    <Button
                      key={`copy-img-${idx}`}
                      size='small'
                      variant='outlined'
                      onClick={async () => {
                        try {
                          await copyImageUrlToClipboard(imgUrl);
                          setSnackbar({ open: true, msg: `Imagen ${idx + 1} copiada. Pegala en WhatsApp (Ctrl/Cmd+V).`, severity: 'success' });
                        } catch (err) {
                          setSnackbar({ open: true, msg: err?.message || 'No se pudo copiar la imagen (revisá permisos/CORS)', severity: 'warning' });
                        }
                      }}
                    >
                      Copiar imagen {idx + 1}
                    </Button>
                  ))}
                </Stack>
                <Typography variant='subtitle2' sx={{ mt: 2, mb: 1 }}>Visualizar y abrir imágenes</Typography>
                <Stack gap={1.25}>
                  {whatsappPreview.images.map((imgUrl, idx) => {
                    const resolvedUrl = resolvePreviewImageUrl(imgUrl);
                    return (
                      <Box
                        key={`preview-img-${idx}`}
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          p: 1,
                          display: 'flex',
                          flexDirection: { xs: 'column', sm: 'row' },
                          gap: 1,
                          alignItems: { xs: 'stretch', sm: 'center' }
                        }}
                      >
                        <Box
                          component='img'
                          src={resolvedUrl}
                          alt={`preview-${idx + 1}`}
                          sx={{ width: 84, height: 84, objectFit: 'cover', borderRadius: 1, bgcolor: '#f5f5f5' }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant='caption' sx={{ wordBreak: 'break-all', display: 'block' }}>
                            {resolvedUrl}
                          </Typography>
                          <Stack direction='row' gap={1} mt={1} flexWrap='wrap'>
                            <Button size='small' variant='outlined' onClick={() => window.open(resolvedUrl, '_blank', 'noopener,noreferrer')}>
                              Abrir
                            </Button>
                            <Button
                              size='small'
                              variant='outlined'
                              onClick={async () => {
                                try {
                                  await navigator.clipboard?.writeText(resolvedUrl);
                                  setSnackbar({ open: true, msg: `URL de imagen ${idx + 1} copiada`, severity: 'success' });
                                } catch {
                                  setSnackbar({ open: true, msg: 'No se pudo copiar la URL', severity: 'warning' });
                                }
                              }}
                            >
                              Copiar URL
                            </Button>
                            <Button
                              size='small'
                              variant='outlined'
                              component='a'
                              href={resolvedUrl}
                              target='_blank'
                              rel='noopener noreferrer'
                              download
                            >
                              Descargar
                            </Button>
                          </Stack>
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Menú contextual (posición exacta del click) */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuPosition)}
        onClose={closeActionOverlays}
        anchorReference={menuPosition ? 'anchorPosition' : 'anchorEl'}
        anchorPosition={menuPosition ? { top: menuPosition.mouseY, left: menuPosition.mouseX } : undefined}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { minWidth: 220 } }}
      >
        <MenuItem onClick={handleViewImages}>
          <ListItemIcon><ImageIcon fontSize='small' /></ListItemIcon>
          <ListItemText primary='Ver imágenes' />
        </MenuItem>
        <MenuItem onClick={handleCopySku}>
          <ListItemIcon><ContentCopyIcon fontSize='small' /></ListItemIcon>
          <ListItemText primary='Copiar SKU' />
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleRepublish}>
          <ListItemIcon><EditIcon fontSize='small' /></ListItemIcon>
          <ListItemText primary='Republicar' />
        </MenuItem>
        <MenuItem onClick={handleDeletePlaceholder}>
          <ListItemIcon><DeleteIcon fontSize='small' /></ListItemIcon>
          <ListItemText primary='Eliminar' />
        </MenuItem>
      </Menu>

      <Dialog
        open={mobileActionsOpen}
        onClose={closeActionOverlays}
        fullWidth
        maxWidth='xs'
      >
        <DialogContent>
          <Stack spacing={1}>
            <Typography variant='subtitle1' fontWeight={700}>Acciones</Typography>
            <Button variant='outlined' onClick={handleViewImages}>Ver imágenes</Button>
            <Button variant='outlined' onClick={handleCopySku}>Copiar SKU</Button>
            <Button variant='contained' onClick={handleRepublish}>Republicar</Button>
            <Button color='error' variant='text' onClick={handleDeletePlaceholder}>Eliminar</Button>
            <Button variant='text' onClick={closeActionOverlays}>Cancelar</Button>
          </Stack>
        </DialogContent>
      </Dialog>

      {/* Modal edición */}
      <Dialog open={openEdit} onClose={() => setOpenEdit(false)} fullScreen={isMobile} fullWidth maxWidth='sm'>
        <DialogContent>
          <Box component='form' onSubmit={handleEditSubmit} display='flex' flexDirection='column' gap={2}>
            <Typography variant='h6'>Publicar/Editar artículo</Typography>
            <TextField
              label='Título'
              value={editValues.name}
              onChange={e => handleEditChange('name', e.target.value)}
              required
              size='small'
            />
            <TextField
              label='Descripción'
              value={editValues.description}
              onChange={e => handleEditChange('description', e.target.value)}
              required
              size='small'
              multiline
              minRows={3}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label='Costo'
                value={editValues.list_price}
                onChange={e => handleEditChange('list_price', e.target.value.replace(/[^0-9.]/g, ''))}
                size='small'
                required
                InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
              />
              <TextField
                label='Precio venta'
                value={editValues.sale_price}
                onChange={e => handleEditChange('sale_price', e.target.value.replace(/[^0-9.]/g, ''))}
                size='small'
                required
                InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
              />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
              <FormControlLabel control={<Checkbox checked={editValues.hasWholesale} onChange={e => handleEditChange('hasWholesale', e.target.checked)} />} label='Tiene precio mayorista' />
              {editValues.hasWholesale && (
                <TextField
                  label='Descuento mayorista (resta)'
                  size='small'
                  type='number'
                  value={editValues.wholesaleDiscount}
                  onChange={e => handleEditChange('wholesaleDiscount', Number(e.target.value) || 0)}
                  sx={{ width: { xs: '100%', sm: 200 } }}
                  InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                />
              )}
            </Stack>
            <Autocomplete
              options={customers}
              getOptionLabel={o => o.name || ''}
              value={customers.find(c => c._id === editValues.customer_id) || null}
              loading={loadingCustomers}
              onOpen={() => { if (!customers.length) loadCustomers(); }}
              onChange={(e, val) => handleEditChange('customer_id', val?._id || '')}
              renderInput={(params) => <TextField {...params} label='Proveedor' size='small' />}
            />
            {/* Gestión de imágenes */}
            <Box>
              <Typography variant='subtitle2' sx={{ mb: 1 }}>Imágenes</Typography>
              <Stack direction='row' flexWrap='wrap' gap={1}>
                {editOrder.map((originalIdx, visualPos) => {
                  const img = editValues.images[originalIdx];
                  const removed = editRemovedIndexes.includes(originalIdx);
                  return (
                    <Box key={`exist-${originalIdx}`} position='relative' sx={{ width: 80, height: 80, borderRadius: 1, overflow: 'hidden', opacity: removed ? 0.3 : 1, border: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <img src={img} alt={`img-${originalIdx}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <Stack spacing={0.25} sx={{ position: 'absolute', left: 2, top: 2 }}>
                        <IconButton size='small' disabled={visualPos === 0} onClick={() => setEditOrder(o => {
                          if (visualPos === 0) return o;
                          const copy = [...o];
                          const tmp = copy[visualPos - 1];
                          copy[visualPos - 1] = copy[visualPos];
                          copy[visualPos] = tmp;
                          return copy;
                        })} sx={{ bgcolor: 'rgba(0,0,0,0.45)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }, width: 24, height: 24 }}>
                          <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                        <IconButton size='small' disabled={visualPos === editOrder.length - 1} onClick={() => setEditOrder(o => {
                          if (visualPos === o.length - 1) return o;
                          const copy = [...o];
                          const tmp = copy[visualPos + 1];
                          copy[visualPos + 1] = copy[visualPos];
                          copy[visualPos] = tmp;
                          return copy;
                        })} sx={{ bgcolor: 'rgba(0,0,0,0.45)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }, width: 24, height: 24 }}>
                          <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Stack>
                      <IconButton size='small' sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'rgba(0,0,0,0.55)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' }, width: 26, height: 26 }} onClick={() => setEditRemovedIndexes(prev => prev.includes(originalIdx) ? prev.filter(i => i !== originalIdx) : [...prev, originalIdx])}>
                        {removed ? '↺' : '✕'}
                      </IconButton>
                      <Box position='absolute' bottom={2} left={4} px={0.5} py={0} bgcolor='rgba(0,0,0,0.5)' borderRadius={1}>
                        <Typography variant='caption' sx={{ fontSize: 10, color: '#fff' }}>{visualPos + 1}</Typography>
                      </Box>
                    </Box>
                  );
                })}
                {editNewImagePreviews.map(({ url }, visualPos) => {
                  return (
                    <Box key={`new-${visualPos}`} position='relative' sx={{ width: 80, height: 80, borderRadius: 1, overflow: 'hidden', border: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <img src={url} alt={`nuevo-${visualPos}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <Stack spacing={0.25} sx={{ position: 'absolute', left: 2, top: 2 }}>
                        <IconButton size='small' disabled={visualPos === 0} onClick={() => setEditNewImages(arr => {
                          if (visualPos === 0) return arr;
                          const copy = [...arr];
                          const tmp = copy[visualPos - 1];
                          copy[visualPos - 1] = copy[visualPos];
                          copy[visualPos] = tmp;
                          return copy;
                        })} sx={{ bgcolor: 'rgba(0,0,0,0.45)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }, width: 24, height: 24 }}>
                          <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                        <IconButton size='small' disabled={visualPos === editNewImages.length - 1} onClick={() => setEditNewImages(arr => {
                          if (visualPos === arr.length - 1) return arr;
                          const copy = [...arr];
                          const tmp = copy[visualPos + 1];
                          copy[visualPos + 1] = copy[visualPos];
                          copy[visualPos] = tmp;
                          return copy;
                        })} sx={{ bgcolor: 'rgba(0,0,0,0.45)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }, width: 24, height: 24 }}>
                          <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Stack>
                      <IconButton size='small' sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'rgba(0,0,0,0.55)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' }, width: 26, height: 26 }} onClick={() => setEditNewImages(prev => prev.filter((_, i) => i !== visualPos))}>✕</IconButton>
                      <Box position='absolute' bottom={2} left={4} px={0.5} py={0} bgcolor='rgba(0,0,0,0.5)' borderRadius={1}>
                        <Typography variant='caption' sx={{ fontSize: 10, color: '#fff' }}>{editOrder.length + visualPos + 1}</Typography>
                      </Box>
                    </Box>
                  );
                })}
                <UploadImage onImagesSelected={setEditNewImages} uploadImage showImages={false}/>
                <TextField
                  size='small'
                  fullWidth
                  placeholder='Prompt IA para imágenes (opcional)'
                  value={imagePrompt}
                  onChange={(e) => setImagePrompt(e.target.value)}
                  sx={{ mt: 0.75 }}
                />
              </Stack>
              {editUploadProgress > 0 && editUploadProgress < 100 && (
                <Box mt={1}>
                  <Typography variant='caption'>Subiendo imágenes: {editUploadProgress}%</Typography>
                  <Box mt={0.5} height={4} bgcolor='divider' borderRadius={2}>
                    <Box height='100%' width={`${editUploadProgress}%`} bgcolor='primary.main' borderRadius={2} />
                  </Box>
                </Box>
              )}
            </Box>
            <Stack direction={{ xs: 'column-reverse', sm: 'row' }} justifyContent='flex-end' gap={1} mt={2}>
              <Button variant='outlined' onClick={() => setOpenEdit(false)} disabled={editLoading}>Cancelar</Button>
              <Button variant='contained' type='submit' disabled={editLoading || (editUploadProgress > 0 && editUploadProgress < 100)} startIcon={editLoading && <CircularProgress size={16} />}>Guardar</Button>
            </Stack>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
    </WhatsappSessionGuard>
  );
};

export default Publications;


// Fila memoizada para reducir re-renders
const PublicationRow = React.memo(({ item, onOpenViewer, preloadImages, isMobile = false, rowIndex = 0, onRowClick }) => {
  const { _id, quantity, prices, sku } = item; // removed news, to chips per request
  const name = item.article?.name || 'Sin nombre';
  const categoryName = item.category?.name || 'Sin categoría';
  const salePrice = prices?.sale_price;
  const images = item.article?.images || [];
  const firstImg = images[0];
  const skuValue = sku || item.article?.sku || 'N/A';
  if (isMobile) {
    return (
      <ListItem
        key={_id}
        alignItems='flex-start'
        divider={false}
        onClick={(e) => onRowClick && onRowClick(e)}
        button
        sx={(theme) => {
        const base = rowIndex % 2 === 0 ? '#ffffff' : '#f8fafc';
        return {
          py: 1.05,
          px: 1,
          bgcolor: base,
          position: 'relative',
          mb: 0.75,
          mx: 0.75,
          borderRadius: 2,
          border: '1px solid #e2e8f0',
          boxShadow: '0 8px 20px rgba(15, 23, 42, 0.06)',
          cursor: 'pointer',
          transition: 'background-color 0.2s, box-shadow 0.2s, transform 0.2s',
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 4,
            bottom: 4,
            width: 0,
            borderTopRightRadius: 4,
            borderBottomRightRadius: 4,
            background: `linear-gradient(180deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.6)})`,
            boxShadow: 'none',
            transition: 'width 0.22s ease, box-shadow 0.22s ease'
          },
          '&:hover': {
            bgcolor: alpha(theme.palette.primary.light, 0.08),
            transform: 'translateY(-1px)',
            boxShadow: '0 12px 24px rgba(15, 23, 42, 0.09)',
            '&::before': { width: 4 }
          },
          '&:focus-visible': {
            outline: 'none',
            boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.35)}`,
            '&::before': { width: 4 }
          },
          '&:active': {
            '&::before': { width: 6 }
          }
        };
      }}>
        <Box display='flex' flexDirection='row' width='100%' gap={1.1}>
          <Box width={86} minWidth={86} height={86}>
            {!firstImg ? (
              <Box
                width='100%'
                height='100%'
                bgcolor='#f1f5f9'
                display='flex'
                alignItems='center'
                justifyContent='center'
                borderRadius={1.8}
                border='1px solid #e2e8f0'
              >
                <img
                  src={logo}
                  alt={name}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 10 }}
                  loading='lazy'
                />
              </Box>
            ) : (
              <Box
                width='100%'
                height='100%'
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenViewer(images, name);
                }}
                onMouseEnter={() => preloadImages && preloadImages(images)}
                sx={{ cursor: 'pointer', position: 'relative', borderRadius: 1.8, overflow: 'hidden', border: '1px solid #dbeafe' }}
              >
                <img
                  src={firstImg}
                  alt={name}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 10 }}
                  loading='lazy'
                />
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 'auto 0 0 0',
                    px: 0.8,
                    py: 0.45,
                    background: 'linear-gradient(180deg, rgba(15,23,42,0) 0%, rgba(15,23,42,0.72) 100%)'
                  }}
                >
                  <Typography variant='caption' sx={{ color: '#fff', fontSize: '0.62rem', fontWeight: 700 }}>
                    Ver
                  </Typography>
                </Box>
                {images.length > 1 && <Chip
                  size='small'
                  label={`+${images.length - 1}`}
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    bgcolor: 'rgba(15,23,42,0.72)',
                    color: '#fff',
                    border: '1px solid rgba(255,255,255,0.45)',
                    height: 19,
                    '& .MuiChip-label': { px: 0.55, fontSize: '0.58rem', fontWeight: 700 }
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenViewer(images, name);
                  }}
                />}
              </Box>
            )}
          </Box>
          <Box flex={1} display='flex' flexDirection='column' gap={0.45} minWidth={0}>
            {/* Línea 1: título + cantidad */}
            <Box display='flex' alignItems='center' gap={0.5} minWidth={0}>
              <Typography variant='subtitle2' noWrap title={name} sx={{ flex: 1, fontSize: '0.88rem', fontWeight: 800, letterSpacing: '0.01em' }}>{name}</Typography>
              <Chip
                size='small'
                label={`Stock ${quantity}`}
                sx={{
                  height: 22,
                  bgcolor: '#ecfeff',
                  color: '#155e75',
                  border: '1px solid #99f6e4',
                  '& .MuiChip-label': { px: 0.65, fontSize: '0.64rem', fontWeight: 700 }
                }}
              />
              <IconButton
                size='small'
                onClick={(e) => {
                  e.stopPropagation();
                  onRowClick && onRowClick(e);
                }}
                sx={{ border: '1px solid #cbd5e1', borderRadius: 1.5, bgcolor: '#fff' }}
              >
                <MoreVertRoundedIcon fontSize='small' />
              </IconButton>
            </Box>
            {/* Línea 2: SKU izquierda, precios derecha */}
            <Box display='flex' alignItems='center' gap={0.5}>
              <Typography variant='caption' noWrap title={skuValue} sx={{ flex: 1, fontSize: '0.64rem', color: '#64748b', fontWeight: 600 }}>{skuValue}</Typography>
              <Box display='flex' gap={0.5} alignItems='center'>
                {salePrice != null && (
                  <Chip
                    size='small'
                    label={`$ ${ccyFormat(salePrice)}`}
                    sx={{
                      height: 22,
                      color: '#fff',
                      background: 'linear-gradient(90deg, #0f766e, #0ea5a4)',
                      '& .MuiChip-label': { px: 0.65, fontSize: '0.68rem', fontWeight: 700 }
                    }}
                  />
                )}
                {prices?.list_price != null && <Chip size='small' variant='outlined' label={`Costo ${ccyFormat(prices.list_price)}`} sx={{ height: 22, bgcolor: '#fff', '& .MuiChip-label': { px: 0.55, fontSize: '0.6rem', fontWeight: 600 }, opacity: 0.95 }} />}
              </Box>
            </Box>
            {/* Línea 3: categoría */}
            <Chip
              size='small'
              label={categoryName}
              sx={{
                height: 20,
                alignSelf: 'flex-start',
                bgcolor: '#f1f5f9',
                color: '#334155',
                border: '1px solid #e2e8f0',
                '& .MuiChip-label': { fontSize: '0.6rem', px: 0.7, fontWeight: 700 }
              }}
            />
          </Box>
        </Box>
      </ListItem>
    );
  }

  return (
    <ListItem
      key={_id}
      alignItems='flex-start'
      divider={false}
      onClick={(e) => onRowClick && onRowClick(e)}
      button
      sx={(theme) => {
      const base = rowIndex % 2 === 0 ? '#ffffff' : '#f8fafc';
      return {
        py: 1.15,
        px: 1.2,
        bgcolor: base,
        position: 'relative',
        mb: 0.8,
        mx: 0.8,
        borderRadius: 2,
        border: '1px solid #e2e8f0',
        boxShadow: '0 12px 24px rgba(15, 23, 42, 0.07)',
        cursor: 'pointer',
        transition: 'background-color 0.2s, box-shadow 0.2s, transform 0.2s',
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 4,
          bottom: 4,
          width: 0,
          borderTopRightRadius: 4,
          borderBottomRightRadius: 4,
          background: `linear-gradient(180deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.6)})`,
          boxShadow: 'none',
          transition: 'width 0.22s ease, box-shadow 0.22s ease'
        },
        '&:hover': {
          bgcolor: alpha(theme.palette.primary.light, 0.08),
          transform: 'translateY(-1px)',
          boxShadow: '0 16px 28px rgba(15, 23, 42, 0.10)',
          '&::before': { width: 4 }
        },
        '&:focus-visible': {
          outline: 'none',
          boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.35)}`,
          '&::before': { width: 4 }
        },
        '&:active': {
          '&::before': { width: 6 }
        }
      };
    }}>
      <Box display='flex' flexDirection='row' width='100%' gap={1.8}>
        {/* Desktop Miniatura */}
        <Box width={148} minWidth={148} height={92} display='flex' alignItems='center'>
          {!firstImg ? (
            <Box width={148} height={92} bgcolor='#f1f5f9' display='flex' alignItems='center' justifyContent='center' borderRadius={2} border='1px solid #e2e8f0'>
              <img
                  src={logo}
                  alt={name}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 12 }}
                  loading='lazy'
                />
            </Box>
          ) : (
            <Box
              width='100%'
              height='100%'
              onClick={(e) => {
                e.stopPropagation();
                onOpenViewer(images, name);
              }}
              onMouseEnter={() => preloadImages && preloadImages(images)}
              sx={{ cursor: 'pointer', position: 'relative', borderRadius: 2, overflow: 'hidden', border: '1px solid #dbeafe' }}
            >
              <img
                src={firstImg}
                alt={name}
                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 12 }}
                loading='lazy'
              />
              <Box
                sx={{
                  position: 'absolute',
                  inset: 'auto 0 0 0',
                  px: 1,
                  py: 0.5,
                  background: 'linear-gradient(180deg, rgba(15,23,42,0) 0%, rgba(15,23,42,0.72) 100%)'
                }}
              >
                <Typography variant='caption' sx={{ color: '#fff', fontSize: '0.66rem', fontWeight: 700 }}>
                  Ver galeria
                </Typography>
              </Box>
              {images.length > 1 && <Chip
                size='small'
                label={`+${images.length - 1}`}
                sx={{
                  position: 'absolute',
                  top: 6,
                  right: 6,
                  bgcolor: 'rgba(15,23,42,0.72)',
                  color: '#fff',
                  border: '1px solid rgba(255,255,255,0.45)',
                  '& .MuiChip-label': { fontWeight: 700 }
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenViewer(images, name);
                }}
              />}
            </Box>
          )}
        </Box>
        <Box flex={1} display='flex' flexDirection='column' gap={0.4} minWidth={0}>
          <Box display='flex' alignItems='center' gap={0.8} minWidth={0} flexWrap='nowrap'>
            <Typography variant='subtitle2' noWrap title={name} sx={{ flex: 1, fontWeight: 800, fontSize: '0.96rem' }}>{name}</Typography>
            <Chip
              label={`Stock ${quantity}`}
              size='small'
              sx={{
                fontSize: '0.63rem',
                height: 23,
                bgcolor: '#ecfeff',
                color: '#155e75',
                border: '1px solid #99f6e4',
                '& .MuiChip-label': { fontWeight: 700 }
              }}
            />
            <IconButton
              size='small'
              onClick={(e) => {
                e.stopPropagation();
                onRowClick && onRowClick(e);
              }}
              sx={{ border: '1px solid #cbd5e1', borderRadius: 1.5, bgcolor: '#fff' }}
            >
              <MoreVertRoundedIcon fontSize='small' />
            </IconButton>
          </Box>
          <Box display='flex' flexDirection='row' alignItems='center' gap={1} flexWrap='nowrap'>
            <Typography variant='caption' noWrap title={skuValue} sx={{ color: '#64748b', fontWeight: 600, maxWidth: '45%' }}>{skuValue}</Typography>
            <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', ml: 'auto' }}>
              {salePrice != null && (
                <Chip
                  label={`$ ${ccyFormat(salePrice)}`}
                  sx={{
                    fontSize: '0.72rem',
                    color: '#fff',
                    fontWeight: 700,
                    height: 24,
                    background: 'linear-gradient(90deg, #0f766e, #0ea5a4)'
                  }}
                />
              )}
              {prices?.list_price != null && <Chip label={`Costo ${ccyFormat(prices.list_price)}`} variant='outlined' sx={{ fontSize: '0.58rem', bgcolor: '#fff', opacity: 0.95, height: 22, '& .MuiChip-label': { fontWeight: 600 } }} />}
            </Box>
          </Box>
          <Chip
            size='small'
            label={categoryName}
            sx={{
              height: 21,
              alignSelf: 'flex-start',
              bgcolor: '#f1f5f9',
              color: '#334155',
              border: '1px solid #e2e8f0',
              '& .MuiChip-label': { fontSize: '0.62rem', px: 0.8, fontWeight: 700 }
            }}
          />
        </Box>
      </Box>
    </ListItem>
  );
});
